import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BotaoDuplo from '../../../Componentes/BotaoDuplo';
import CampoEdicoes, {
  InputCampoEdicoes,
  SubTituloCampoEdicoes,
} from '../../../Componentes/CampoEdicoes';
import CorpoPagina from '../../../Componentes/CorpoPagina';
import DateInput from '../../../Componentes/DateInput';
import Espaçador from '../../../Componentes/Espaçador';
import ModalConfirmacao from '../../../Componentes/ModalConfirmacao';
import SelectInput from '../../../Componentes/SelectInput';
import TextInput from '../../../Componentes/TextInput';
import TopicoTexto from '../../../Componentes/TopicoTexto';
import { apiService } from '../../../Services/ApiService';
import locations from '../../../constants/locations';
import moment, { now } from 'moment';
import useWindowSize from '../../../Hooks/windowSize';
import { useTranslation } from 'react-i18next';

export default function SensoresCadastro() {
  const larguraEspaçador = 16;
  const larguraInput = '35%';

  const [endnodesList, setEndnodesList] = useState([]);
  const [endnodeId, setEndnodeId] = useState('');

  const [environmentsList, setEnvironmentsList] = useState([]);
  const [environmentId, setEnvironmentId] = useState('');

  const [selectedLocation, setSelectedLocation] = useState({
    value: '',
    label: '',
  });

  const [sensor, setSensor] = useState({
    user: {
      name: '',
    },
    environment: {
      name: '',
    },
    endnode: {
      endnodeIdentification: '',
      macAddress: '',
    },
    sensorName: '',
    location: '',
    calibrationStatus: null,
    lastCalibrationDate: null,
    nextCalibrationDate: null,
    maxTemperature: null,
    minTemperature: null,
    notifyTemperature: false,
    endOfCourseMaxTime: null,
    notifyEndOfCourse: false,
  });

  const [validationErrors, setValidationErrors] = useState({
    sensorName: true,
    environment: true,
    location: true,
    minTemperature: true,
    maxTemperature: true,
    endOfCourseMaxTime: true,
    endnode: true,
    nextCalibrationDate: true,
  });

  const [touchedFields, setTouchedFields] = useState({
    sensorName: false,
    minTemperature: false,
    maxTemperature: false,
    endOfCourseMaxTime: false,
    nextCalibrationDate: false,
  });

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const { t: tError } = useTranslation('errors');

  const validators = {
    sensorName: (value) => value && value.trim().length > 0 && value.trim().length <= 100,
    environment: (value) => value && value.id,
    location: (value) => value && value.length > 0 && value !== '',
    minTemperature: (value) =>
      selectedLocation.value === 'REFRIGERATOR'
        ? value !== null &&
          sensor.maxTemperature !== null &&
          Number(value) < Number(sensor.maxTemperature)
        : true,
    maxTemperature: (value) =>
      selectedLocation.value === 'REFRIGERATOR'
        ? value !== null &&
          sensor.minTemperature !== null &&
          Number(value) > Number(sensor.minTemperature)
        : true,
    endOfCourseMaxTime: (value) =>
      selectedLocation.value === 'REFRIGERATOR'
        ? !value || (value !== null && Number(value) >= 0)
        : true,
    endnode: (value) => value && value.id,
    nextCalibrationDate: (value) =>
      value && moment(value).startOf('day').isAfter(moment().startOf('day')),
  };

  const [isEditing, setIsEditing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [modalCancel, setModalCancel] = useState(false);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [modalError, setModalError] = useState({
    visible: false,
    message: '',
  });
  const [isMobile, setIsMobile] = useState(false);

  const navega = useNavigate();
  const { id } = useParams();
  const { get, post, put } = apiService();
  const windowSize = useWindowSize();

  const parseLocations = (location) => {
    switch (location) {
      case 'ENVIRONMENT':
        return 'Ambiente';
      case 'REFRIGERATOR':
        return 'Geladeira';
      default:
        return '';
    }
  };

  const sensorLocations = Object.keys(locations).map((location) => ({
    value: location,
    label: parseLocations(location.toString()),
  }));

  useEffect(() => {
    const fetchData = async () => {
      await getEnvironments();

      if (id) {
        await getSensorById();
        setIsEditing(true);
      } else {
        setIsLoading(false);
        setIsEditing(false);
        setIsDataLoaded(true);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (isDataLoaded) {
      setIsFormValid(Object.values(validationErrors).every((v) => !v));
    }
  }, [validationErrors, isDataLoaded]);

  useEffect(() => {
    if (isDataLoaded) {
      Object.entries(sensor).forEach(([key, value]) => {
        setAndValidateField(key, value);
      });
    }
  }, [
    endnodeId,
    environmentId,
    selectedLocation,
    sensor.minTemperature,
    sensor.maxTemperature,
    sensor.endOfCourseMaxTime,
    isDataLoaded,
  ]);

  useEffect(() => {
    if (selectedLocation.value) {
      getEndnodes();
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (windowSize.width < 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowSize]);

  const getEndnodes = async () => {
    try {
      const endnodes = await get(`endnodes/available/${selectedLocation.value}`);

      if (endnodes && endnodes.length) {
        const list = endnodes.map((endnode) => ({
          value: endnode.id,
          label: endnode.endnodeIdentification,
        }));
        setEndnodesList(list);
      } else {
        setEndnodesList([
          {
            value: '',
            label: 'Nenhum endnode disponível',
          },
        ]);
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar endnodes' });
    }
  };

  const getEnvironments = async () => {
    try {
      const environments = await get('environments/all');

      if (environments && environments.length) {
        const list = environments.map((environment) => ({
          value: environment.id,
          label: environment.name,
        }));
        setEnvironmentsList(list);
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar ambientes' });
    }
  };

  const getSensorById = async () => {
    try {
      setIsLoading(true);

      const savedSensor = await get(`sensors/${id}`);

      if (savedSensor) {
        setSensor({
          environment: {
            id: savedSensor.environment.id,
            name: savedSensor.environment.name,
          },
          endnode: {
            id: savedSensor.endnode.id,
            endnodeIdentification: savedSensor.endnode.endnodeIdentification,
            macAddress: savedSensor.endnode.macAddress,
          },
          sensorName: savedSensor.name,
          location: savedSensor.location,
          lastCalibrationDate: savedSensor.lastCalibrationDate
            ? savedSensor.lastCalibrationDate
            : null,
          nextCalibrationDate: savedSensor.nextCalibrationDate
            ? savedSensor.nextCalibrationDate
            : null,
          maxTemperature:
            savedSensor.maxTemperature !== null ? Number(savedSensor.maxTemperature) : null,
          minTemperature:
            savedSensor.minTemperature !== null ? Number(savedSensor.minTemperature) : null,
          notifyTemperature: savedSensor.notifyTemperature,
          endOfCourseMaxTime:
            savedSensor.endOfCourseMaxTime !== null ? Number(savedSensor.endOfCourseMaxTime) : null,
          notifyEndOfCourse: savedSensor.notifyEndOfCourse,
        });

        Object.entries(sensor).forEach(([key, value]) => {
          setTouchedFields((prev) => ({ ...prev, [key]: true }));
        });

        setSelectedLocation({
          value: savedSensor.location,
          label: parseLocations(savedSensor.location),
        });
        setEndnodeId({ value: savedSensor.endnode.id });
        setEnvironmentId({ value: savedSensor.environment.id });
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar sensor' });
      console.log(error);
    } finally {
      setIsLoading(false);
      setIsDataLoaded(true);
    }
  };

  const validateField = (name, value) => {
    return validators[name] ? validators[name](value) : true;
  };

  const setAndValidateField = (name, value) => {
    setSensor((prev) => ({ ...prev, [name]: value }));
    const isValid = validateField(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: !isValid }));
  };

  const handleChange = (name, value) => {
    setAndValidateField(name, value);
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  const handleSelectChange = (name, value) => {
    if (name === 'environmentId') {
      setEnvironmentId(value);
      handleChange('environment', { id: value.value, name: value.label });
    } else if (name === 'endnodeId') {
      if (value.value === '') {
        return;
      }
      setEndnodeId(value.value);
      handleChange('endnode', { id: value.value, endnodeIdentification: value.label });
    } else if (name === 'userId') {
      setUserId(value.value);
      handleChange('user', { id: value.value, name: value.label });
    } else if (name === 'location') {
      handleChange('location', value.value);
      setSelectedLocation({ value: value.value, label: value.label });
    }
  };

  const handleSave = () => {
    setModalConfirm(true);
  };

  const saveSensor = async () => {
    if (!isFormValid) {
      setModalConfirm(false);
      return;
    }

    const data = {
      id,
      endnodeId: endnodeId.value,
      environmentId: environmentId.value,
      name: sensor.sensorName,
      location: sensor.location,
      nextCalibrationDate: sensor.nextCalibrationDate ? sensor.nextCalibrationDate : null,
      maxTemperature: Number(sensor.maxTemperature),
      minTemperature: Number(sensor.minTemperature),
      notifyTemperature: sensor.notifyTemperature,
      endOfCourseMaxTime: sensor.endOfCourseMaxTime,
      notifyEndOfCourse: sensor.notifyEndOfCourse,
    };

    try {
      setIsLoading(true);
      if (id) {
        await put(`sensors/${data.id}`, data);
      } else {
        await post('sensors', data);
      }

      setModalSuccess(true);
    } catch (error) {
      if (error.message && tError(error.message)) {
        setModalError({
          visible: true,
          message: tError(error.message),
        });
      } else {
        setModalError({
          visible: true,
          message: 'Erro ao salvar sensor',
        });
      }
    } finally {
      setModalConfirm(false);
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setModalCancel(true);
  };

  return (
    <CorpoPagina loading={isLoading}>
      {modalCancel && (
        <ModalConfirmacao
          aviso="Deseja realmente cancelar?"
          confirmar={false}
          clicado={() => navega(-1)}
          clicadoVolta={() => setModalCancel(false)}
        />
      )}
      {modalSuccess && (
        <ModalConfirmacao
          aviso="Sensor salvo com sucesso!"
          confirmar={true}
          clicado={() => navega(-1)}
        />
      )}
      {modalConfirm && (
        <ModalConfirmacao
          aviso="Deseja realmente salvar o sensor?"
          confirmar={false}
          clicado={() => saveSensor()}
          clicadoVolta={() => setModalConfirm(false)}
        />
      )}
      {modalError.visible && (
        <ModalConfirmacao
          aviso={modalError.message}
          erro={true}
          clicado={() =>
            setModalError({
              visible: false,
              message: '',
            })
          }
        />
      )}
      <div className="ContainerGeral">
        {!isEditing && <TopicoTexto titulo="Cadastrar Sensor" marginLeft={15} />}
        {isEditing && <TopicoTexto titulo="Editar Sensor" marginLeft={15} />}

        <div className="MeioLinha">
          <div
            className="form-column"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              width:
                !selectedLocation.value || selectedLocation.value === 'ENVIRONMENT'
                  ? '45%'
                  : '100%',
            }}
          >
            <CampoEdicoes
              titulo="DADOS GERAIS"
              maxH={'100%'}
              maxW={'100%'}
              width={'100%'}
              height={'100%'}
              top={65}
            >
              <SubTituloCampoEdicoes nome="IDENTIFICAÇÃO" />

              <SelectInput
                titulo="Selecione o ambiente"
                itens={environmentsList}
                padrao={
                  sensor.environment.name !== ''
                    ? { value: sensor.environment.id, label: sensor.environment.name }
                    : { value: '', label: 'Selecione' }
                }
                largura="90%"
                clicado={(environment) => {
                  handleSelectChange('environmentId', environment);
                  setEnvironmentId(environment);
                }}
              />

              <SelectInput
                titulo="Selecione o equipamento"
                itens={sensorLocations}
                padrao={
                  selectedLocation.value !== ''
                    ? selectedLocation
                    : { value: '', label: 'Selecione' }
                }
                largura="90%"
                clicado={(txt) => handleSelectChange('location', txt)}
              />

              <TextInput
                titulo="Nome"
                placeholder="Insira o Nome"
                tipo="input"
                padrao={sensor.sensorName}
                largura="90%"
                top={8}
                onUpdate={(txt) => handleChange('sensorName', txt)}
                erro={touchedFields.sensorName && validationErrors.sensorName}
                erroTexto="Nome inválido"
                maxLength={100}
              />

              <SelectInput
                titulo="Selecione o ID"
                itens={endnodesList}
                padrao={
                  sensor && sensor.endnode.id
                    ? { value: sensor.endnode.id, label: sensor.endnode.endnodeIdentification }
                    : { value: '', label: 'Selecione' }
                }
                largura="90%"
                clicado={(endnode) => {
                  handleSelectChange('endnodeId', endnode);
                  setEndnodeId(endnode);
                }}
                disabled={selectedLocation.value === ''}
              />

              <SubTituloCampoEdicoes nome="CALIBRAÇÃO" top={64} />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '90%',
                  justifyContent: 'space-between',
                }}
              >
                {sensor.lastCalibrationDate ? (
                  <>
                    <DateInput
                      titulo="Última calibração"
                      padrao={sensor.lastCalibrationDate}
                      largura="100%"
                      top={20}
                      clicado={(date) => handleChange('lastCalibrationDate', date)}
                      disabled={true}
                    />

                    <Espaçador espaço={larguraEspaçador} />
                  </>
                ) : null}

                <DateInput
                  titulo="Próxima calibração"
                  padrao={sensor.nextCalibrationDate}
                  largura="100%"
                  top={20}
                  clicado={(date) => handleChange('nextCalibrationDate', date)}
                  disabled={false}
                  erro={touchedFields.nextCalibrationDate && validationErrors.nextCalibrationDate}
                  erroTexto="Data inválida"
                />
              </div>
            </CampoEdicoes>
          </div>
          {selectedLocation.value === 'REFRIGERATOR' && (
            <div className="form-column">
              <CampoEdicoes
                titulo="PARÂMETROS"
                maxH={'100%'}
                maxW={'100%'}
                width={'100%'}
                height={'100%'}
              >
                <SubTituloCampoEdicoes nome="TEMPERATURA (°C)" />

                <div
                  style={{
                    width: '90%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                  }}
                >
                  <TextInput
                    titulo="Mínimo"
                    padrao={sensor.minTemperature}
                    placeholder="Insira o valor mínimo"
                    tipo="number"
                    largura={larguraInput}
                    onUpdate={(value) => handleChange('minTemperature', value)}
                    erro={touchedFields.minTemperature && validationErrors.minTemperature}
                    erroTexto="Valor inválido"
                  />

                  <Espaçador espaço={larguraEspaçador} />

                  <TextInput
                    titulo="Máximo"
                    padrao={sensor.maxTemperature}
                    placeholder="Insira o valor máximo"
                    tipo="number"
                    largura={larguraInput}
                    onUpdate={(value) => handleChange('maxTemperature', value)}
                    erro={touchedFields.maxTemperature && validationErrors.maxTemperature}
                    erroTexto="Valor inválido"
                  />
                </div>

                <InputCampoEdicoes
                  desc="Enviar notificação por e-mail em caso de alerta."
                  isChecked={sensor.notifyTemperature}
                  top={8}
                  clicado={(status) => {
                    if (status) {
                      handleChange('notifyTemperature', true);
                    } else {
                      handleChange('notifyTemperature', false);
                    }
                  }}
                />

                {sensor.location === 'REFRIGERATOR' ? (
                  <>
                    <SubTituloCampoEdicoes nome="PORTA ABERTA (s)" top={65} />
                    <div
                      style={{
                        width: '90%',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <TextInput
                        titulo="Tempo Máximo"
                        padrao={sensor.endOfCourseMaxTime}
                        placeholder="Tempo (s)"
                        tipo="number"
                        largura={'50%'}
                        onUpdate={(value) => handleChange('endOfCourseMaxTime', value)}
                        erro={
                          touchedFields.endOfCourseMaxTime && validationErrors.endOfCourseMaxTime
                        }
                        erroTexto="Valor inválido"
                        minValue={0}
                      />
                    </div>
                    <InputCampoEdicoes
                      desc="Enviar notificação por e-mail em caso de alerta."
                      isChecked={sensor.notifyEndOfCourse}
                      top={8}
                      clicado={(status) => {
                        if (status) {
                          handleChange('notifyEndOfCourse', true);
                        } else {
                          handleChange('notifyEndOfCourse', false);
                        }
                      }}
                    />
                  </>
                ) : null}
              </CampoEdicoes>
            </div>
          )}
        </div>

        <BotaoDuplo
          nome="Salvar"
          nomeVolta="Cancelar"
          top={16}
          bottom={32}
          clicado={() => handleSave()}
          clicadoVolta={() => handleCancel()}
          externo={true}
          disabled={!isFormValid}
        />
      </div>
    </CorpoPagina>
  );
}
